import { onBeforeUnmount, watch, type Ref } from 'vue';

/**
 * Mirrors the polling pattern used by `GaiaVaultFilePdfPreview` for preview-status
 * checks: while `isActive` is true, invoke `refresh()` on a fixed interval.
 * Stops automatically when `isActive` flips to false or when the consuming
 * component unmounts.
 *
 * Used by the template/variant detail views to track async template-validation
 * status (`pending` / `in_progress` → `done` / `error`).
 */
const POLL_INTERVAL_MS = 3000;

export function useTemplateValidationPolling(
  isActive: Ref<boolean>,
  refresh: () => Promise<void> | void
): void {
  let intervalId: number | null = null;

  function start(): void {
    if (intervalId !== null) return;
    intervalId = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  watch(
    isActive,
    active => {
      if (active) start();
      else stop();
    },
    { immediate: true }
  );

  onBeforeUnmount(stop);
}
